/** JSON payload returned by GET /screenshot (mirrors ScreenshotResponse in src/server.ts). */
interface ScreenshotResponse {
  headingText: string;
  infoTexts: string[];
  xCoordinate: string;
  yCoordinate: string;
  floors: string[];
  shortInfos: string[];
  entrances: string[];
  entranceCount: number;
  /** PNG screenshot as a `data:image/png;base64,...` data URI. */
  image: string;
}

/**
 * Base URL for the screenshot API, replaced at build time by esbuild's
 * `define` (see frontend/build.mjs). Empty string => same-origin relative
 * requests, which is also the fallback in dev where no `define` runs.
 */
declare const __API_BASE__: string;
const API_BASE = typeof __API_BASE__ !== "undefined" ? __API_BASE__ : "";

/**
 * Converts BGS2005 / CCS2005 coordinates (EPSG:7801 — Lambert Conformal Conic
 * 2SP over the GRS80 ellipsoid, the CRS the KAIS cadastre reports) to WGS84
 * latitude/longitude. `northing`/`easting` follow the Bulgarian geodetic
 * convention where X points north and Y east. Returns null on non-finite input.
 *
 * Implements the inverse formulas from EPSG Guidance Note 7-2 (method 9802).
 */
function bgs2005ToWgs84(
  northing: number,
  easting: number,
): { lat: number; lon: number } | null {
  if (!Number.isFinite(northing) || !Number.isFinite(easting)) return null;

  // GRS80 ellipsoid.
  const a = 6378137.0;
  const e = Math.sqrt(0.0066943800229007876); // first eccentricity
  const deg = Math.PI / 180;

  // EPSG:7801 defining parameters.
  const phi1 = 42.0 * deg; // standard parallel 1
  const phi2 = 43.33333333333333 * deg; // standard parallel 2
  const phiF = 42.66787568333333 * deg; // latitude of false origin
  const lamF = 25.5 * deg; // longitude of false origin
  const EF = 500000.0; // easting at false origin
  const NF = 4725824.3591; // northing at false origin

  const m = (phi: number) =>
    Math.cos(phi) / Math.sqrt(1 - e * e * Math.sin(phi) ** 2);
  const t = (phi: number) =>
    Math.tan(Math.PI / 4 - phi / 2) /
    ((1 - e * Math.sin(phi)) / (1 + e * Math.sin(phi))) ** (e / 2);

  const m1 = m(phi1);
  const m2 = m(phi2);
  const n = (Math.log(m1) - Math.log(m2)) / (Math.log(t(phi1)) - Math.log(t(phi2)));
  const F = m1 / (n * t(phi1) ** n);
  const rF = a * F * t(phiF) ** n;

  const dE = easting - EF;
  const dN = rF - (northing - NF);
  const rp = Math.sign(n) * Math.sqrt(dE * dE + dN * dN);
  const tp = (rp / (a * F)) ** (1 / n);
  const thetap = Math.atan2(dE, dN);

  const lon = thetap / n + lamF;

  // Iterate for latitude (converges in a few steps).
  let phi = Math.PI / 2 - 2 * Math.atan(tp);
  for (let i = 0; i < 8; i++) {
    const esin = e * Math.sin(phi);
    phi = Math.PI / 2 - 2 * Math.atan(tp * ((1 - esin) / (1 + esin)) ** (e / 2));
  }

  return { lat: phi / deg, lon: lon / deg };
}

/**
 * Parses the KAIS X/Y coordinate strings and returns WGS84 lat/lon, or null if
 * they don't resolve to a point inside Bulgaria. Tries both axis orderings so a
 * swapped X/Y (or an upstream convention change) still lands correctly.
 */
function coordsToGps(
  xStr: string,
  yStr: string,
): { lat: number; lon: number } | null {
  const parse = (s: string) => parseFloat((s ?? "").replace(",", ".").trim());
  const x = parse(xStr);
  const y = parse(yStr);
  const inBulgaria = (g: { lat: number; lon: number } | null) =>
    !!g && g.lat > 41 && g.lat < 44.5 && g.lon > 22 && g.lon < 28.7;

  const asNorthEast = bgs2005ToWgs84(x, y);
  if (inBulgaria(asNorthEast)) return asNorthEast;
  const swapped = bgs2005ToWgs84(y, x);
  if (inBulgaria(swapped)) return swapped;
  return null;
}

/** Envelope pushed by the /stream WebSocket (mirrors src/server.ts). */
type StreamMessage =
  | { type: "frame"; data: string; metadata?: unknown }
  | { type: "log"; level: string; line: string }
  | { type: "done"; result: ScreenshotResponse }
  | { type: "error"; error: string };

const form = document.getElementById("form") as HTMLFormElement;
const button = form.querySelector("button") as HTMLButtonElement;
const status = document.getElementById("status") as HTMLDivElement;
const result = document.getElementById("result") as HTMLImageElement;
const live = document.getElementById("live") as HTMLCanvasElement;
const logsEl = document.getElementById("logs") as HTMLPreElement;
const details = document.getElementById("details") as HTMLDivElement;
const heading = document.getElementById("heading") as HTMLHeadingElement;
const infos = document.getElementById("infos") as HTMLUListElement;
const searchInput = form.elements.namedItem("searchText") as HTMLInputElement;
const widthInput = form.elements.namedItem("width") as HTMLInputElement;
const heightInput = form.elements.namedItem("height") as HTMLInputElement;
const liveToggle = form.elements.namedItem("live") as HTMLInputElement;
const logsToggle = form.elements.namedItem("logs") as HTMLInputElement;

const liveCtx = live.getContext("2d");

/** Appends a log line to #logs, revealing it on first line and auto-scrolling. */
function appendLogLine(level: string, line: string): void {
  const div = document.createElement("div");
  div.className = `log-${level}`;
  div.textContent = `[${level}] ${line}`;
  logsEl.appendChild(div);
  logsEl.style.display = "block";
  logsEl.scrollTop = logsEl.scrollHeight;
}

/** Renders the final PNG + scraped metadata into the page. */
function renderResult(data: ScreenshotResponse): void {
  heading.textContent = data.headingText;

  const lines = [
    ...data.infoTexts,
    `X: ${data.xCoordinate} Y: ${data.yCoordinate}`,
  ];
  if (data.floors.length > 0) {
    lines.push(`Най-нисък етаж: ${data.floors[0]}`);
    lines.push(`Най-висок етаж: ${data.floors.at(-1)}`);
    lines.push(`Етажи: ${data.floors.join(", ")}`);
  }

  //if ((data.entrances?.length ?? 0) > 0) {
  //  lines.push(`Обозначения на входовете: ${data.entrances.join(", ")}`);
  //}

  if (data.entranceCount > 0) {
    lines.push(`Входове: ${data.entranceCount}`);
  }

  infos.replaceChildren(
    ...lines.map((text) => {
      const li = document.createElement("li");
      li.textContent = text;
      return li;
    }),
  );

  details.querySelector("a.maps-link")?.remove();
  try {
    // Prefer the exact GPS point converted from the BGS2005 coordinates; fall
    // back to the address text only if the conversion doesn't yield a valid point.
    const gps = coordsToGps(data.xCoordinate, data.yCoordinate);
    const query = gps
      ? `${gps.lat.toFixed(7)},${gps.lon.toFixed(7)}`
      : data.headingText.split(" ").slice(2).join(" ").trim();
    if (query) {
      const a = document.createElement("a");
      a.className = "maps-link";
      a.href =
        "https://www.google.com/maps/search/?api=1&query=" +
        encodeURIComponent(query);
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.textContent = "Отвори в Google Maps";
      details.appendChild(a);
    }
  } catch {
    // no maps link on failure
  }

  details.style.display = "block";

  result.src = data.image;
  result.style.display = "block";
}

/** Builds the ws://|wss:// URL for /stream, deriving from API_BASE. */
function streamUrl(params: URLSearchParams): string {
  const base = new URL(API_BASE || window.location.origin, window.location.href);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = "/stream";
  const streamParams = new URLSearchParams(params);
  // Send `live` explicitly: the server streams frames unless it sees live=0, so
  // logs-only mode must opt out rather than just omit the param.
  streamParams.set("live", liveToggle.checked ? "1" : "0");
  if (logsToggle.checked) streamParams.set("logs", "1");
  base.search = streamParams.toString();
  return base.toString();
}

/** One-shot request/response path — returns the final result as JSON. */
async function runFetch(params: URLSearchParams): Promise<void> {
  const res = await fetch(`${API_BASE}/screenshot?${params}`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  renderResult(await res.json());
  status.textContent = "";
}

/** Live path — streams screencast frames, then renders the final result. */
function runStream(params: URLSearchParams): Promise<void> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(streamUrl(params));
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    ws.onmessage = async (ev) => {
      let msg: StreamMessage;
      try {
        msg = JSON.parse(ev.data as string);
      } catch {
        return;
      }

      if (msg.type === "frame") {
        if (!liveCtx) return;
        try {
          const blob = await (
            await fetch(`data:image/jpeg;base64,${msg.data}`)
          ).blob();
          const bmp = await createImageBitmap(blob);
          live.width = bmp.width;
          live.height = bmp.height;
          liveCtx.drawImage(bmp, 0, 0);
          bmp.close();
          // Reveal only once we actually have a frame, so an error before the
          // first frame doesn't flash an empty canvas — and so the last frame
          // stays on screen (below the error) if the run fails mid-stream.
          live.style.display = "block";
        } catch {
          /* skip a frame that fails to decode */
        }
      } else if (msg.type === "log") {
        appendLogLine(msg.level, msg.line);
      } else if (msg.type === "done") {
        renderResult(msg.result);
        live.style.display = "none";
        status.textContent = "";
        finish(resolve);
        ws.close();
      } else if (msg.type === "error") {
        finish(() => reject(new Error(msg.error)));
        ws.close();
      }
    };

    ws.onerror = () => finish(() => reject(new Error("Връзката е неуспешна")));
    ws.onclose = () =>
      finish(() => reject(new Error("Връзката е прекъсната преди завършване")));
  });
}

form.addEventListener("submit", async (e) => {
  e.preventDefault();
  const searchText = searchInput.value.trim();
  if (!searchText) return;

  const width = widthInput.value.trim();
  const height = heightInput.value.trim();
  if ((width === "") !== (height === "")) {
    status.textContent = "Въведете едновременно ширина и височина, или нито едно от двете.";
    return;
  }

  button.disabled = true;
  status.classList.add("loading");
  status.textContent = liveToggle.checked
    ? "Предаване на живо…"
    : "Заснемане на екрана…";
  result.style.display = "none";
  live.style.display = "none";
  details.style.display = "none";
  logsEl.textContent = "";
  logsEl.style.display = "none";

  try {
    const params = new URLSearchParams({ search: searchText });
    if (width && height) {
      params.set("width", width);
      params.set("height", height);
    }
    await (liveToggle.checked || logsToggle.checked
      ? runStream(params)
      : runFetch(params));
  } catch (err) {
    // Leave the live canvas as-is: if any frame streamed before the failure,
    // its last frame stays visible below this error message. It was hidden on
    // submit and only revealed once a frame drew, so nothing shows otherwise.
    status.textContent = `Неуспешно: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    status.classList.remove("loading");
    button.disabled = false;
  }
});

// Make this file a module so its top-level names (e.g. `status`) don't clash
// with the DOM globals.
export {};
